/**
 * CR-6192: Daily OTIF time-series chart for the Supplier Detail → Performance
 * tab (port of SP CR-5303/6010). Renders per-day OTIF % over a rolling window
 * and lets the user click a day to drill through to that day's PO lines.
 *
 * The series and the drill-through both go through the OTIF drill-down metric
 * (`otif_lines`), so a point's value and the lines it opens always agree — the
 * scoping/exclusion consistency CR-5303 was about.
 */
import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { Loader2, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { drilldownAPI, SupplierProfile, DrilldownTrendPoint } from '../../lib/api';
import { useView } from '../../contexts/ViewContext';
import { OTIFDrilldownModal } from '../../components/drilldown';
import { clampToDailyChartWindow } from './performancePeriods';

interface DailyOtifChartProps {
  supplier: SupplierProfile;
  /**
   * CR-5563: the chart now follows the Performance tab's period control
   * instead of carrying its own 30/90/180 toggle. Two date controls stacked on
   * one screen, with different options and different defaults, let a user put
   * the chart on 30 days while the cards below said Last 12 Months.
   */
  dateFrom: string;
  dateTo: string;
}

/** Short axis/tooltip label, e.g. "15 Apr". */
function shortLabel(iso: string): string {
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short' });
}

/** Pull the hovered/clicked day out of a Recharts mouse state, if any. */
function readActiveDate(state: unknown): string | null {
  const payload = (state as {
    activePayload?: Array<{ payload?: { date?: string } }>;
  })?.activePayload?.[0]?.payload;
  return payload?.date ?? null;
}

export function DailyOtifChart({ supplier, dateFrom, dateTo }: DailyOtifChartProps) {
  const { viewLevel, getViewQueryParams } = useView();
  const [series, setSeries] = useState<DrilldownTrendPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Drill-through state — the day whose PO lines are being inspected.
  const [drillDate, setDrillDate] = useState<string | null>(null);

  // CR-5563: the daily series is floored to the last calendar year — see
  // clampToDailyChartWindow for why. The KPI cards above keep the full window.
  const { dateFrom: effectiveFrom, clamped: isClamped } = useMemo(
    () => clampToDailyChartWindow({ dateFrom, dateTo }),
    [dateFrom, dateTo],
  );

  const fetchTrend = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await drilldownAPI.getTrend('otif_lines', {
        supplier: supplier.company,
        period: 'day',
        date_from: effectiveFrom,
        date_to: dateTo,
        view_level: viewLevel,
        ...getViewQueryParams(),
      });
      setSeries(response.data.series || []);
    } catch (err: unknown) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr.response?.data?.error || 'Failed to load OTIF trend');
    } finally {
      setLoading(false);
    }
  }, [supplier.company, effectiveFrom, dateTo, viewLevel, getViewQueryParams]);

  useEffect(() => {
    fetchTrend();
  }, [fetchTrend]);

  // Only days that have a computed OTIF value plot a point; arrears-only days
  // (no delivered lines) carry null and Recharts leaves a gap.
  const chartData = series.map((p) => ({
    date: p.date,
    label: shortLabel(p.date),
    otif: p.otif,
    total_lines: p.total_lines,
    otif_lines: p.otif_lines,
  }));
  const hasData = chartData.some((p) => p.otif !== null);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <CardTitle className="text-sm font-medium flex items-center gap-1.5">
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
            Daily OTIF
          </CardTitle>
        </div>
        <p className="text-xs text-muted-foreground">
          OTIF % by original due date · click a day to see the PO lines due that day
          {isClamped && ' · showing the most recent 12 months of the selected period'}
        </p>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="h-[260px] flex items-center justify-center text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading trend…
          </div>
        ) : error ? (
          <div className="h-[260px] flex items-center justify-center text-sm text-destructive">
            {error}
          </div>
        ) : !hasData ? (
          <div className="h-[260px] flex items-center justify-center text-sm text-muted-foreground">
            No OTIF data for this supplier in the selected period.
          </div>
        ) : (
          <div
            // Clicking anywhere in the plot area drills into the day whose
            // plotted dot is nearest the click on the x axis — resolved from
            // geometry, not from tooltip or hover state. Without this only a
            // pixel-perfect hit on a dot worked, which on a sparse series is
            // most of the chart doing nothing — the failure QA reported.
            onClick={(e) => {
              // Resolve the click to the nearest plotted day using the rendered
              // dot geometry. Recharts 3 only reports activePayload when the
              // pointer is over a rendered element, so anything derived from
              // its mouse state is null for a click in empty plot area — which
              // on a sparse series is most of the chart.
              const host = e.currentTarget as HTMLElement;
              // Sort by rendered x before indexing into the data. The dots are
              // matched to `plotted` positionally, so relying on DOM order
              // would silently drill into the wrong day if recharts ever
              // emitted them out of order — a wrong date is worse than a
              // dead click, and it would look like correct behaviour.
              const dots = Array.from(
                host.querySelectorAll<SVGCircleElement>('.recharts-line-dot'),
              )
                .map((dot) => {
                  const r = dot.getBoundingClientRect();
                  return { centre: r.x + r.width / 2 };
                })
                .sort((a, b) => a.centre - b.centre);
              if (!dots.length) return;
              const plotted = chartData.filter((p) => p.otif !== null);
              let best = -1;
              let bestDist = Infinity;
              dots.forEach((dot, i) => {
                const d = Math.abs(dot.centre - e.clientX);
                if (d < bestDist) { bestDist = d; best = i; }
              });
              const point = plotted[best];
              if (point?.date) setDrillDate(point.date);
            }}
            style={{ cursor: 'pointer' }}
          >
          <ResponsiveContainer width="100%" height={260}>
            <LineChart
              data={chartData}
              margin={{ top: 8, right: 12, bottom: 4, left: -16 }}
              onClick={(state) => {
                // PR #636 review: read the clicked point's payload directly
                // (its ISO `date`) rather than matching the formatted axis
                // label — more reliable and matches the recharts pattern used
                // elsewhere (e.g. dashboard CostTab).
                // Direct hit on the line or a dot. Recharts only fires this
                // when the click lands on a rendered element, which is why the
                // wrapper below also handles clicks in empty plot area.
                const payload = readActiveDate(state);
                if (payload) setDrillDate(payload);
              }}
            >
              <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
              <XAxis dataKey="label" tick={{ fontSize: 11 }} minTickGap={20} />
              <YAxis domain={[0, 100]} tick={{ fontSize: 11 }} unit="%" />
              <Tooltip
                formatter={(value: number | string, _name, item) => {
                  const p = (item?.payload || {}) as { otif_lines?: number; total_lines?: number };
                  return [`${value}% (${p.otif_lines}/${p.total_lines} lines)`, 'OTIF'];
                }}
                labelFormatter={(label) => `Due day: ${label}`}
              />
              <ReferenceLine y={95} stroke="#dc2626" strokeDasharray="4 4"
                label={{ value: 'Target 95%', position: 'insideTopRight', fontSize: 10, fill: '#dc2626' }} />
              <Line
                type="monotone"
                dataKey="otif"
                stroke="#1d4ed8"
                strokeWidth={2}
                dot={{ r: 3, cursor: 'pointer' }}
                activeDot={{ r: 5, cursor: 'pointer' }}
                connectNulls={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
          </div>
        )}
      </CardContent>

      {drillDate && (
        <OTIFDrilldownModal
          isOpen={!!drillDate}
          onClose={() => setDrillDate(null)}
          filters={{
            supplier: supplier.company,
            date_from: drillDate,
            date_to: drillDate,
            // W3-T7: a day point carries no arrears stock in Historical; the
            // drill says so too, or the point and its rows disagree.
            grain: 'day',
          }}
          contextLabel={`${supplier.company_name} · ${shortLabel(drillDate)}`}
          initialTab="total"
        />
      )}
    </Card>
  );
}
